import React, { useCallback, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    IconButton,
    InputBase,
    Snackbar,
    Alert,
    Typography,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { Virtuoso } from 'react-virtuoso';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { waForwardMessageTool } from '@common/tools/whatsapp';
import {
    selectForwardingMessage,
    selectNonArchivedChatsSortedByTimestamp,
} from './redux/TeamInboxSelectors';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { openTeamInboxChatThunk } from './redux/TeamInboxThunks';

// WhatsApp Web caps "forward to" selections at 5 chats.
const MAX_FORWARD_TARGETS = 5;

/** Resolve a chat's WhatsApp JID across the shapes the inbox cache holds
 *  (mirrors getChatJid in TeamInboxSelectors — kept local to avoid touching
 *  the selectors file). */
const getChatJid = (chat: any): string => {
    if (typeof chat?.chatId === 'string') return chat.chatId;
    if (typeof chat?._id === 'string') return chat._id;
    if (typeof chat?._serialized === 'string') return chat._serialized;
    if (typeof chat?.id?._serialized === 'string') return chat.id._serialized;
    if (typeof chat?.id === 'string') return chat.id;
    return '';
};

const getChatName = (chat: any, jid: string): string =>
    chat?.name || chat?.formattedTitle || jid.split('@')[0] || '';

type ForwardChatRowProps = {
    jid: string;
    name: string;
    selected: boolean;
    disabled: boolean;
    onToggle: (jid: string) => void;
};

/** Single selectable chat row — memoized so toggling one row doesn't re-render
 *  the whole virtualized list. */
const ForwardChatRow = React.memo(({ jid, name, selected, disabled, onToggle }: ForwardChatRowProps) => {
    const handleClick = useCallback(() => {
        if (disabled) return;
        onToggle(jid);
    }, [disabled, jid, onToggle]);

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                height: 60,
                px: 2,
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                borderBottom: '1px solid var(--wa-divider, #e9edef)',
                transition: 'background-color 0.12s ease',
                '&:hover': {
                    backgroundColor: disabled ? 'transparent' : 'var(--wa-row-hover-bg, rgba(0,0,0,0.04))',
                },
            }}
        >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <ContactAvatar size={40} textSize={15} contactId={jid} dontFetchProfilePicFromDB />
                {selected && (
                    <CheckCircleIcon
                        sx={{
                            position: 'absolute',
                            bottom: -2,
                            insetInlineEnd: -2,
                            fontSize: 18,
                            color: 'var(--wa-accent, #00a884)',
                            backgroundColor: 'var(--wa-panel-bg, #fff)',
                            borderRadius: '50%',
                        }}
                    />
                )}
            </Box>
            <Typography
                sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: 16,
                    color: 'var(--wa-title-text, #111b21)',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                }}
                title={name}
            >
                {name}
            </Typography>
        </Box>
    );
});
ForwardChatRow.displayName = 'ForwardChatRow';

const ForwardMessageDialog: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();

    const forwardingMessage = useAppSelector(selectForwardingMessage);
    const chats = useAppSelector(selectNonArchivedChatsSortedByTimestamp);

    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [pending, setPending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);

    const open = !!forwardingMessage;

    // Reset transient state whenever the dialog closes.
    const resetState = useCallback(() => {
        setSearch('');
        setSelectedIds([]);
        setPending(false);
        setError(null);
    }, []);

    const handleClose = useCallback(() => {
        if (pending) return;
        dispatch(TeamInboxActions.setForwardingMessage(null));
        resetState();
    }, [dispatch, pending, resetState]);

    const handleToggle = useCallback((jid: string) => {
        setError(null);
        setSelectedIds((prev) => {
            if (prev.includes(jid)) return prev.filter((id) => id !== jid);
            if (prev.length >= MAX_FORWARD_TARGETS) return prev;
            return [...prev, jid];
        });
    }, []);

    // { jid, name } for every non-archived chat, then filtered by the search
    // token (case-insensitive name match OR chatId digits match).
    const chatItems = useMemo(() => {
        return chats
            .map((chat) => {
                const jid = getChatJid(chat);
                return { jid, name: getChatName(chat, jid) };
            })
            .filter((c) => c.jid);
    }, [chats]);

    const filteredItems = useMemo(() => {
        const token = search.trim().toLowerCase();
        if (!token) return chatItems;
        const digits = token.replace(/\D/g, '');
        return chatItems.filter(({ jid, name }) => {
            if (name.toLowerCase().includes(token)) return true;
            if (digits && jid.replace(/\D/g, '').includes(digits)) return true;
            return false;
        });
    }, [chatItems, search]);

    const selectedNames = useMemo(() => {
        const byJid = new Map(chatItems.map((c) => [c.jid, c.name]));
        return selectedIds.map((jid) => byJid.get(jid) || jid.split('@')[0]);
    }, [chatItems, selectedIds]);

    const reachedLimit = selectedIds.length >= MAX_FORWARD_TARGETS;

    const handleSend = useCallback(async () => {
        if (pending || selectedIds.length === 0) return;
        const waMessageKey: string | undefined = forwardingMessage?.waMessageKey?._serialized;
        if (!waMessageKey) {
            setError(t('teamInbox.forwardMissingKey', 'This message cannot be forwarded.'));
            return;
        }

        setPending(true);
        setError(null);
        try {
            const res: any = await waForwardMessageTool({
                waMessageKey,
                toChatIds: selectedIds,
            });
            if (!res || res.success === false || res?.data?.success === false) {
                throw new Error(res?.error || 'forward failed');
            }

            // Nice-to-have: after a single-target forward, jump into that chat.
            const jumpTo = selectedIds.length === 1 ? selectedIds[0] : null;

            dispatch(TeamInboxActions.setForwardingMessage(null));
            resetState();
            setShowSuccess(true);

            if (jumpTo) {
                dispatch(openTeamInboxChatThunk({ chatId: jumpTo }));
            }
        } catch (err) {
            setPending(false);
            setError(t('teamInbox.forwardFailed', 'Failed to forward message. Please try again.'));
        }
    }, [pending, selectedIds, forwardingMessage, dispatch, resetState, t]);

    const renderRow = useCallback(
        (_index: number, item: { jid: string; name: string }) => {
            const selected = selectedIds.includes(item.jid);
            return (
                <ForwardChatRow
                    jid={item.jid}
                    name={item.name}
                    selected={selected}
                    disabled={!selected && reachedLimit}
                    onToggle={handleToggle}
                />
            );
        },
        [selectedIds, reachedLimit, handleToggle],
    );

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="xs"
                aria-labelledby="forward-message-dialog-title"
                PaperProps={{
                    sx: {
                        backgroundColor: 'var(--wa-panel-bg, #fff)',
                        backgroundImage: 'none',
                        height: 640,
                        maxHeight: '85vh',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        edge="start"
                        onClick={handleClose}
                        disabled={pending}
                        sx={{ color: 'var(--wa-secondary-text, #54656f)' }}
                        aria-label={t('common.close', 'Close')}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography id="forward-message-dialog-title" sx={{ fontSize: 18, fontWeight: 600, color: 'var(--wa-title-text, #111b21)' }}>
                        {t('teamInbox.forwardMessageTo', 'Forward message to')}
                    </Typography>
                </Box>

                {/* Search */}
                <Box sx={{ px: 2, pb: 1, flexShrink: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'var(--wa-search-bg, #f0f2f5)',
                            borderRadius: '8px',
                            px: 1.5,
                            height: 40,
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 20, color: 'var(--wa-secondary-text, #54656f)' }} />
                        <InputBase
                            autoFocus
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('teamInbox.searchChats', 'Search')}
                            sx={{
                                flex: 1,
                                fontSize: 15,
                                color: 'var(--wa-title-text, #111b21)',
                                '& input::placeholder': {
                                    color: 'var(--wa-secondary-text, #54656f)',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>
                </Box>

                {/* Chat list */}
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {filteredItems.length === 0 ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: 'var(--wa-secondary-text, #54656f)',
                                fontSize: 14,
                            }}
                        >
                            {t('teamInbox.noChatsFound', 'No chats found')}
                        </Box>
                    ) : (
                        <Virtuoso
                            data={filteredItems}
                            style={{ height: '100%' }}
                            itemContent={renderRow}
                        />
                    )}
                </Box>

                {/* Footer (only when at least one chat is selected) */}
                {selectedIds.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.5,
                            flexShrink: 0,
                            borderTop: '1px solid var(--wa-divider, #e9edef)',
                            backgroundColor: 'var(--wa-composer-input-bg, #fff)',
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: 'var(--wa-title-text, #111b21)',
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                }}
                                title={selectedNames.join(', ')}
                            >
                                {selectedNames.join(', ')}
                            </Typography>
                            {error && (
                                <Typography sx={{ fontSize: 12, color: theme.palette.error.main, mt: 0.25 }}>
                                    {error}
                                </Typography>
                            )}
                        </Box>
                        <IconButton
                            onClick={handleSend}
                            disabled={pending}
                            sx={{
                                width: 48,
                                height: 48,
                                flexShrink: 0,
                                backgroundColor: 'var(--wa-accent, #00a884)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'var(--wa-accent, #00a884)', filter: 'brightness(0.95)' },
                                '&.Mui-disabled': { backgroundColor: 'var(--wa-accent, #00a884)', opacity: 0.6, color: '#fff' },
                            }}
                            aria-label={t('teamInbox.forward', 'Forward')}
                        >
                            {pending ? (
                                <CircularProgress size={22} sx={{ color: '#fff' }} />
                            ) : (
                                <SendIcon
                                    sx={{
                                        fontSize: 22,
                                        // Paper-plane points toward the send direction: flip in RTL.
                                        transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
                                    }}
                                />
                            )}
                        </IconButton>
                    </Box>
                )}
            </Dialog>

            <Snackbar
                open={showSuccess}
                autoHideDuration={3000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setShowSuccess(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {t('teamInbox.messageForwarded', 'Message forwarded')}
                </Alert>
            </Snackbar>
        </>
    );
};

export default React.memo(ForwardMessageDialog);
